import type { LicenceState } from '../practitioners/licence.ts';
import { hasCountedDiagnosis, type DiagnosisFact } from './diagnoses.ts';

// Finishing a consultation and closing a visit (docs/plans/phase1-consultation.md §4.3,
// §5.1, §5.5). Pure: the API loads the facts, these say what is missing.

/** The finish rule IDs. Stable: acknowledgement rates are measured by them. */
export const FINISH_RULES = [
  'consult.finish.critical_open',
  'consult.finish.no_diagnosis',
  'consult.finish.unsigned',
  'consult.finish.licence',
] as const;
export type FinishRule = (typeof FINISH_RULES)[number];

export type FinishDetail =
  | 'critical_open'
  | 'no_diagnosis'
  | 'note_draft'
  | 'no_note'
  | 'licence_expired'
  | 'licence_not_recorded';

export interface FinishItem {
  ruleId: FinishRule;
  /** Every finish item can be acknowledged with a reason. */
  tier: 'interruptive';
  detail: FinishDetail;
}

export interface FinishCheckInput {
  diagnoses: readonly Pick<DiagnosisFact, 'certainty' | 'status'>[];
  /** The consultation's note: none written, a draft, or signed (amended counts as signed). */
  note: 'none' | 'draft' | 'signed';
  /** Critical lab notifications for this patient nobody has acknowledged. */
  openCriticalNotifications: number;
  /** The finishing clinician's licence, from their practitioner profile (plan §2.13). */
  licence: LicenceState;
}

/**
 * What is missing before a consultation ends, each an interruptive item needing an
 * acknowledgement with a reason. Finishing never acknowledges a critical result.
 * TODO(clinical-verify): C16 (no diagnosis) and C22 (critical result open) are
 * interruptive, not blocking (accepted defaults 25 Sep 2026).
 * TODO(clinical-verify): P13 and plan §4.3 make an expired or unrecorded licence
 * interruptive, not blocking; a licence that only expires soon does not interrupt (it is
 * shown in Settings > Practitioners).
 */
export function finishCheck(input: FinishCheckInput): FinishItem[] {
  const items: FinishItem[] = [];
  if (input.openCriticalNotifications > 0) {
    items.push({
      ruleId: 'consult.finish.critical_open',
      tier: 'interruptive',
      detail: 'critical_open',
    });
  }
  if (!hasCountedDiagnosis(input.diagnoses)) {
    items.push({
      ruleId: 'consult.finish.no_diagnosis',
      tier: 'interruptive',
      detail: 'no_diagnosis',
    });
  }
  if (input.note !== 'signed') {
    items.push({
      ruleId: 'consult.finish.unsigned',
      tier: 'interruptive',
      detail: input.note === 'draft' ? 'note_draft' : 'no_note',
    });
  }
  if (input.licence === 'expired') {
    items.push({
      ruleId: 'consult.finish.licence',
      tier: 'interruptive',
      detail: 'licence_expired',
    });
  } else if (input.licence === 'not_recorded' || input.licence === 'no_profile') {
    items.push({
      ruleId: 'consult.finish.licence',
      tier: 'interruptive',
      detail: 'licence_not_recorded',
    });
  }
  return items;
}

export type CloseBlocker =
  'consultation_in_progress' | 'in_queue' | 'patient_owes' | 'charges_not_priced';
export type CloseReasonItem = 'orders_open' | 'no_diagnosis';

export interface VisitCloseInput {
  consultationsInProgress: number;
  activeQueueEntries: number;
  /** What the patient still owes, in cents. */
  owedCents: bigint;
  /** Charges still "Not priced": what the patient owes isn't known yet. */
  unpricedCharges: number;
  /** Requested procedures and imaging not performed, and active lab orders. */
  openOrders: number;
  hasDiagnosis: boolean;
}

/**
 * What stops a visit closing, and what needs a reason. A balance blocks for now: closing
 * with a balance needs billing's invoice at close (billing decision 18), which isn't built,
 * so the cashier takes payment first (consultation plan §10).
 */
export function visitCloseCheck(input: VisitCloseInput): {
  blocking: CloseBlocker[];
  needsReason: CloseReasonItem[];
} {
  const blocking: CloseBlocker[] = [];
  if (input.consultationsInProgress > 0) blocking.push('consultation_in_progress');
  if (input.activeQueueEntries > 0) blocking.push('in_queue');
  if (input.owedCents > 0n) blocking.push('patient_owes');
  if (input.unpricedCharges > 0) blocking.push('charges_not_priced');
  const needsReason: CloseReasonItem[] = [];
  if (input.openOrders > 0) needsReason.push('orders_open');
  if (!input.hasDiagnosis) needsReason.push('no_diagnosis');
  return { blocking, needsReason };
}
